import React, { useState, useEffect } from 'react';
import { useAuth, useLanguage, api } from '../../context/AppContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Shield, Target, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import TacticalBriefingModal from '../TacticalBriefingModal';

const MISSIONS = [
  { value: 'perte_poids', label_fr: 'Perte de poids radicale', label_en: 'Radical Weight Loss', icon: '🎯', desc_fr: 'Brûler les réserves et alléger l\'armure', desc_en: 'Burn reserves and lighten the armor' },
  { value: 'prise_masse', label_fr: 'Prise de masse / Force', label_en: 'Mass / Strength', icon: '💪', desc_fr: 'Bâtir la charpente et augmenter la puissance', desc_en: 'Build the frame and increase power' },
  { value: 'mobilité', label_fr: 'Mobilité / Santé articulaire', label_en: 'Mobility / Joint Health', icon: '🦵', desc_fr: 'Réhabilitation, longévité et fluidité', desc_en: 'Rehabilitation, longevity and fluidity' },
  { value: 'endurance', label_fr: 'Endurance Tactique', label_en: 'Tactical Endurance', icon: '🫀', desc_fr: 'Conditionnement cardio, préparation d\'épreuve', desc_en: 'Cardio conditioning, event preparation' },
  { value: 'maintien', label_fr: 'Maintien Operationnel', label_en: 'Operational Maintenance', icon: '⚖️', desc_fr: 'Recomposition corporelle et maintien des acquis', desc_en: 'Body recomposition and maintaining gains' },
];

const SettingsMissionPrincipale = () => {
  const { user, updateUser } = useAuth();
  const { language } = useLanguage();
  const [currentMission, setCurrentMission] = useState(null);
  const [saving, setSaving] = useState(null);
  const [loading, setLoading] = useState(true);
  const [briefing, setBriefing] = useState(null);
  const [provPlan, setProvPlan] = useState([]);
  const [provPlanId, setProvPlanId] = useState(null);
  const [briefingMission, setBriefingMission] = useState(null);
  const [briefingCreatedAt, setBriefingCreatedAt] = useState(null);

  useEffect(() => {
    const loadMission = async () => {
      try {
        const res = await api.get('/users/terrain-profile');
        const obj = res.data.terrain_profile?.main_objective;
        setCurrentMission(obj || null);
      } catch {
        // silent
      } finally {
        setLoading(false);
      }
    };
    loadMission();
  }, []);

  // Check for pending (non-accepted) briefing on mount
  useEffect(() => {
    const checkPending = async () => {
      try {
        const res = await api.get('/users/pending-briefing');
        if (res.data.pending) {
          setBriefing(res.data.briefing);
          setProvPlan(res.data.provisional_plan || []);
          setProvPlanId(res.data.provisional_plan_id || null);
          setBriefingMission(res.data.new_mission);
          setBriefingCreatedAt(res.data.created_at);
        }
      } catch {
        // silent
      }
    };
    checkPending();
  }, []);

  const handleMissionChange = async (missionValue) => {
    if (missionValue === currentMission) return;
    const oldMission = currentMission;
    setSaving(missionValue);
    try {
      const terrain = user?.terrain_profile || {};
      // Migrate legacy values (adulte/adolescent/senior) to the new 2-tier system
      const legacyMap = { adulte: 'performance', adolescent: 'performance', senior: 'longevite' };
      const ageCat = legacyMap[terrain.age_category] || terrain.age_category || 'performance';
      await api.post('/users/terrain-profile', {
        age_category: ageCat,
        sex: terrain.sex || 'autre',
        main_objective: missionValue,
      });
      setCurrentMission(missionValue);
      updateUser({
        ...user,
        terrain_profile: { ...terrain, main_objective: missionValue },
      });

      // Trigger Tactical Briefing
      try {
        const briefRes = await api.post('/users/tactical-briefing', {
          old_mission: oldMission || 'inconnu',
          new_mission: missionValue,
        });
        if (briefRes.data.briefing) {
          setBriefing(briefRes.data.briefing);
          setProvPlan(briefRes.data.provisional_plan || []);
          setProvPlanId(briefRes.data.provisional_plan_id || null);
          setBriefingMission(missionValue);
          setBriefingCreatedAt(briefRes.data.created_at || new Date().toISOString());
        }
      } catch {
        // Briefing failed silently — still show a toast
        const mission = MISSIONS.find(m => m.value === missionValue);
        toast.success(
          language === 'fr'
            ? `Mission redefinie : ${mission?.label_fr}. L'IA s'adapte.`
            : `Mission redefined: ${mission?.label_en}. AI adapting.`
        );
      }
    } catch {
      toast.error(language === 'fr' ? 'Erreur de sauvegarde' : 'Save error');
    } finally {
      setSaving(null);
    }
  };

  return (
    <>
      <Card className="bg-card border-border" data-testid="settings-mission-principale">
        <CardHeader>
          <CardTitle className="font-heading text-xl uppercase tracking-tight flex items-center gap-2">
            <Target className="w-5 h-5 text-red-500" />
            {language === 'fr' ? 'Mission Principale' : 'Main Mission'}
          </CardTitle>
          <CardDescription>
            {language === 'fr'
              ? 'Change ta mission pour reajuster l\'algorithme de YUMIX et le discours de l\'IA.'
              : 'Change your mission to readjust YUMIX algorithm and AI tone.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {loading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="w-6 h-6 animate-spin text-primary" />
            </div>
          ) : (
            <>
              <div className="space-y-2">
                {MISSIONS.map(m => {
                  const isActive = currentMission === m.value;
                  const isSaving = saving === m.value;
                  return (
                    <button
                      key={m.value}
                      type="button"
                      onClick={() => handleMissionChange(m.value)}
                      disabled={saving !== null}
                      data-testid={`mission-option-${m.value}`}
                      className={`w-full p-3 rounded-xl text-left transition-all border-2 ${
                        isActive
                          ? 'border-red-500 bg-red-500/10 shadow-[0_0_15px_rgba(239,68,68,0.1)]'
                          : 'border-transparent bg-secondary/50 hover:border-muted-foreground/30'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-xl">{m.icon}</span>
                        <div className="flex-1 min-w-0">
                          <p className={`font-heading text-sm uppercase tracking-tight ${isActive ? 'text-red-400' : 'text-foreground'}`}>
                            {language === 'fr' ? m.label_fr : m.label_en}
                          </p>
                          <p className="text-[11px] text-muted-foreground mt-0.5 truncate">
                            {language === 'fr' ? m.desc_fr : m.desc_en}
                          </p>
                        </div>
                        {isSaving && <Loader2 className="w-4 h-4 animate-spin text-red-400" />}
                        {isActive && !isSaving && <Check className="w-4 h-4 text-red-400" />}
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Mental Module Banner */}
              <div className="p-3 rounded-xl border border-amber-500/30 bg-amber-500/5" data-testid="settings-mental-module-banner">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-amber-400 flex-shrink-0" />
                  <p className="text-xs text-amber-400 font-medium">
                    {language === 'fr'
                      ? 'Module de Renforcement Mental : Inclus et active par defaut pour toutes les missions.'
                      : 'Mental Reinforcement Module: Included and active by default for all missions.'}
                  </p>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {/* Tactical Briefing Modal */}
      {briefing && (
        <TacticalBriefingModal
          briefing={briefing}
          provisionalPlan={provPlan}
          provisionalPlanId={provPlanId}
          newMission={briefingMission}
          createdAt={briefingCreatedAt}
          onClose={() => { setBriefing(null); setProvPlan([]); setProvPlanId(null); setBriefingCreatedAt(null); }}
          onAccepted={() => {}}
          language={language}
        />
      )}
    </>
  );
};

export default SettingsMissionPrincipale;
